import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  FilePlus2,
  GitCompareArrows,
  Pencil,
  RotateCcw,
  Trash2,
  ArrowUpDown,
} from "lucide-react";
import { fieldLabel } from "./historyFields";
import { formatHistoryDay, formatHistoryTime } from "../../../utils/dateUtils";
import {
  authorLabel,
  type HistoryAction,
  type HistoryCollection,
  type HistoryVersion,
} from "../../../core/history/entities/HistoryVersion";

/// Icône par nature d'événement. Plus lisible qu'un badge textuel répété sur chaque ligne.
const ACTION_ICONS: Record<HistoryAction, typeof Pencil> = {
  create: FilePlus2,
  update: Pencil,
  delete: Trash2,
  restore: RotateCcw,
  reorder: ArrowUpDown,
};

const ACTION_TONES: Record<HistoryAction, string> = {
  create: "text-emerald-600 dark:text-emerald-400",
  update: "text-blue-600 dark:text-blue-400",
  delete: "text-red-600 dark:text-red-400",
  restore: "text-amber-600 dark:text-amber-400",
  reorder: "text-gray-400 dark:text-gray-500",
};

interface DayGroup {
  day: string;
  versions: { version: HistoryVersion; index: number }[];
}

/// Regroupe les événements par jour, en conservant l'index d'origine pour la sélection.
function groupByDay(
  versions: HistoryVersion[],
  language: string,
  todayLabel: string,
  yesterdayLabel: string,
): DayGroup[] {
  const groups: DayGroup[] = [];

  versions.forEach((version, index) => {
    const day = formatHistoryDay(
      version.created,
      language,
      todayLabel,
      yesterdayLabel,
    );
    const last = groups[groups.length - 1];

    if (last && last.day === day) {
      last.versions.push({ version, index });
      return;
    }

    groups.push({ day, versions: [{ version, index }] });
  });

  return groups;
}

interface HistoryTimelineProps {
  collection: HistoryCollection;
  versions: HistoryVersion[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  /// Version épinglée comme second terme de la comparaison, `null` si aucune.
  pinnedId: string | null;
  onTogglePin: (id: string) => void;
  language: string;
}

/// Liste des événements, groupés par jour.
///
/// Deux affordances par ligne : le corps sélectionne la version à examiner (premier terme de la
/// comparaison), l'icône de fin l'épingle comme second terme. Épingler l'emporte sur le mode de
/// comparaison courant — c'est une intention explicite.
export function HistoryTimeline({
  collection,
  versions,
  selectedIndex,
  onSelect,
  pinnedId,
  onTogglePin,
  language,
}: HistoryTimelineProps) {
  const { t } = useTranslation();
  const groups = useMemo(
    () =>
      groupByDay(versions, language, t("history.today"), t("history.yesterday")),
    [versions, language, t],
  );

  return (
    <div className="space-y-4">
      {groups.map((group) => (
        <section key={group.day}>
          <h4 className="mb-1.5 px-1 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
            {group.day}
          </h4>
          <ul className="space-y-1">
            {group.versions.map(({ version, index }) => {
              const isSelected = index === selectedIndex;
              const isPinned = version.id === pinnedId;
              const Icon = ACTION_ICONS[version.action] ?? Pencil;

              const summary = version.changedFields.length
                ? version.changedFields
                    .map((field) => fieldLabel(collection, field, t))
                    .join(", ")
                : t(`history.summary.${version.action}`);

              return (
                <li
                  key={version.id}
                  className={`flex items-stretch rounded-lg transition-colors ${
                    isSelected
                      ? "bg-blue-50 dark:bg-gray-700"
                      : isPinned
                        ? "bg-blue-50/50 ring-1 ring-inset ring-blue-200 dark:bg-blue-950/30 dark:ring-blue-900"
                        : "hover:bg-gray-50 dark:hover:bg-gray-800"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => onSelect(index)}
                    aria-current={isSelected}
                    className="min-w-0 flex-1 rounded-s-lg px-2.5 py-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500"
                  >
                    <span className="flex items-baseline gap-2">
                      <Icon
                        className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${ACTION_TONES[version.action]}`}
                        aria-hidden="true"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                            {t(`history.action.${version.action}`)}
                          </span>
                          <span className="shrink-0 text-xs tabular-nums text-gray-400 dark:text-gray-500">
                            {formatHistoryTime(version.created, language)}
                          </span>
                        </span>
                        <span className="mt-0.5 block truncate text-xs text-gray-500 dark:text-gray-400">
                          {authorLabel(version.author, t("history.unknownAuthor"))}
                        </span>
                        <span className="mt-0.5 block truncate text-xs text-gray-400 dark:text-gray-500">
                          {summary}
                        </span>
                      </span>
                    </span>
                  </button>

                  {/* Second terme de la comparaison. Masqué — mais son gabarit conservé, pour que
                      les lignes ne se décalent pas — sur la version sélectionnée, qui en est le
                      premier terme. */}
                  <button
                    type="button"
                    role="switch"
                    aria-checked={isPinned}
                    aria-label={t("history.compareWithThis", {
                      date: formatHistoryTime(version.created, language),
                    })}
                    title={t("history.compareWithThis", {
                      date: formatHistoryTime(version.created, language),
                    })}
                    onClick={() => onTogglePin(version.id)}
                    tabIndex={isSelected ? -1 : undefined}
                    className={`flex w-9 shrink-0 items-center justify-center rounded-e-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 ${
                      isSelected ? "invisible" : ""
                    } ${
                      isPinned
                        ? "text-blue-600 dark:text-blue-400"
                        : "text-gray-300 hover:text-blue-600 dark:text-gray-600 dark:hover:text-blue-400"
                    }`}
                  >
                    <GitCompareArrows className="h-4 w-4" aria-hidden="true" />
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

/// Squelette de chargement : garde la forme de la liste plutôt que d'afficher un spinner.
export function HistoryTimelineSkeleton() {
  return (
    <div className="space-y-2" aria-hidden="true">
      {[0, 1, 2, 3].map((row) => (
        <div
          key={row}
          className="animate-pulse rounded-lg bg-gray-100 px-2.5 py-2 dark:bg-gray-800"
        >
          <div className="h-3 w-24 rounded bg-gray-200 dark:bg-gray-700" />
          <div className="mt-2 h-2.5 w-32 rounded bg-gray-200 dark:bg-gray-700" />
        </div>
      ))}
    </div>
  );
}
